import { useState, type FormEvent } from "react";

import { Alert, Button, Card, CardTitle, Input, Label } from "@realroof/ui";

import { api, ROLE_LABEL } from "../api";
import { useSession } from "../session";

const RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

/** The staff member's own account: who they are, and changing their password. */
export function MyAccount() {
  const { me } = useSession();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    if (!RULE.test(next)) return setMessage({ kind: "error", text: "Use at least 8 characters with a letter and a number." });
    if (next !== again) return setMessage({ kind: "error", text: "The two new passwords do not match." });
    setBusy(true);
    try {
      await api("/admin/me/password", { method: "POST", body: { current_password: current, new_password: next } });
      setMessage({ kind: "success", text: "Password changed. Use it next time you log in (here and on the website)." });
      setCurrent("");
      setNext("");
      setAgain("");
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex max-w-xl flex-col gap-4">
      <h1 className="text-2xl font-bold">My account</h1>
      <Card className="gap-1">
        <strong>{me?.full_name || me?.email}</strong>
        <span className="text-sm text-muted-foreground">
          {me?.email} · {me?.mobile} · {me ? ROLE_LABEL[me.staff_role] : ""}
        </span>
      </Card>
      <Card>
        <form className="flex flex-col gap-3" onSubmit={save} aria-label="Change password">
          <CardTitle className="text-base">Change password</CardTitle>
          <Label>
            Current password
            <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </Label>
          <Label>
            New password
            <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
          </Label>
          <Label>
            Confirm new password
            <Input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
          </Label>
          <span className="text-sm text-muted-foreground">At least 8 characters, with a letter and a number.</span>
          {message && <Alert tone={message.kind}>{message.text}</Alert>}
          <Button type="submit" variant="primary" className="self-start" disabled={busy}>
            {busy ? "Saving…" : "Change password"}
          </Button>
        </form>
      </Card>
    </section>
  );
}
